/**
 * How the product addresses the person who is signed in.
 *
 * Two forms, and both are the design's: the **given name** the home screen
 * greets ("Good morning, Isabelle."), and the **short name** the rail pill
 * prints ("Marie L."). They lived in two different files deriving names two
 * different ways, which is how one of them ended up wrong.
 *
 * ── The field arrived, so the guessing left ───────────────────────────────
 * Every prototype supplies `firstName` as its own field; `GET /auth/session`
 * carried only `fullName`, so for one day this file held a list of honorifics
 * and stripped a leading run of them. That list is gone: the session now
 * answers **`givenName`**, non-nullable, backed by `users.given_name` when
 * somebody has corrected it and by the backend's own derivation when nobody
 * has (request 12 §1).
 *
 * The list was the right thing to delete rather than keep as a belt: two
 * derivations of one fact is exactly the drift this module exists to remove,
 * and the client's copy had nowhere to put a correction.
 */

/**
 * `Marie Lefebvre` + `Marie` → `Marie L.` — the rail pill's form.
 *
 * The rail is 220px wide and the name shares it with an avatar, a plan line
 * and a chevron, so a full family name truncated mid-word. Shortening is also
 * the softer choice on a screen a child may be looking over: the app says who
 * is signed in without printing the family's surname on it.
 *
 * Takes the **server's** given name rather than deriving one, so a titled
 * member reads `Hélène D.` and not `Dr. D.` — the bug the greeting had, one
 * line lower in the same rail — and reads whatever correction was stored,
 * which a local derivation could never see.
 *
 * Deliberately naive about what a family name is: the LAST whitespace-separated
 * part, first character kept. A one-word name (mononyms, and anyone who filled
 * the field with a first name only) is returned untouched rather than reduced
 * to an initial, which would leave the rail saying "M." and nothing else. The
 * same guard covers the case where the family name IS the given name — a
 * corrected `givenName` that is the whole of a two-token name would otherwise
 * produce `Sarah S.`
 *
 * Not locale-aware, and does not need to be — `charAt` on a composed accent
 * keeps the accent, and both shipping locales write given-name-first.
 */
export function shortName(fullName: string, givenName: string, email?: string): string {
  const full = fullName.trim()
  const given = givenName.trim()

  /*
   * No given name: print the full name whole, and NEVER initial it.
   *
   * Without knowing which token is the given name there is nothing to keep and
   * nothing to abbreviate — an earlier version fell back to the full name and
   * then appended a family initial anyway, producing `Camille Tremblay T.`
   *
   * `email` is the last resort and it has a live caller: accepting an
   * organisation seat creates the account from an address alone, so both names
   * are `''` until somebody fills one in. The rail pill is the one place that
   * must always name who is signed in — blank there reads as signed out.
   */
  if (!given) return full || (email ?? '').split('@')[0] || ''

  const parts = full.split(/\s+/).filter(Boolean)
  if (parts.length < 2) return given

  const family = parts[parts.length - 1]!
  if (family === given) return given

  return `${given} ${family.charAt(0).toUpperCase()}.`
}
